/**
 * Projects tab, plus the Rooms, Shipyard, Crew and Fittings views the Station
 * (panels/station.ts) shows between galaxies.
 */

import { ROOMS, ROOM_BUILD_COSTS, roomValue, type RoomDef } from '../../content/rooms';
import { ITEMS, FIT_MAX_LEVEL, FIT_THRESHOLDS, FLEET_STAT_LABEL, fitBonus, fitLevel, socketCost } from '../../content/resources';
import { BRANCH_LABEL, PROJECTS, type Branch } from '../../content/projects';
import {
  PROSPECTORS, PROSPECTOR_RARITY_COLOR, PROSPECTOR_ROLE_LABEL, RECRUIT_COST_SC, prospectorBonus,
} from '../../content/prospectors';
import { PLANETS, PLANET_STAT_LABEL } from '../../content/planets';
import { canBuyProject, projectCost, projectStatus } from '../../sim/projects';
import { projectDef } from '../../content/projects';
import { nextRoomCost, offeredRooms, roomLevel, roomMult, roomsBuilt } from '../../sim/rooms';
import { SHIPS, SHIP_EFFECT_LABEL, type ShipDef, type ShipEffect } from '../../content/ships';
import { canBuyShip, goalProgress, ownsShip } from '../../sim/ships';
import { esc, fmtFine, price } from '../format';
import { hullSvg } from '../hulls';
import { icon } from '../icons';
import { btn, ingredients, resName, type View } from '../view';

function roomValueText(def: RoomDef, level: number): string {
  const value = roomValue(def, level);
  if (def.shape === 'offline') return `${fmtFine(value)}h`;
  if (def.shape === 'cut') return `−${Math.round(value * 100)}%`;
  return `×${fmtFine(value)}`;
}

function roomTile(v: View, def: RoomDef): string {
  const level = roomLevel(v.state, def.id);
  const cost = nextRoomCost(v.state, def.id);
  const built = level > 0;
  const offered = !built && offeredRooms(v.state).includes(def.id);
  if (!built && !offered) return '';
  const now = built ? roomValueText(def, level) : '—';
  const next = cost === null ? '' : roomValueText(def, level + 1);
  const label = cost === null ? 'Max' : built ? `${price('te', cost)}` : `Build ${price('te', cost)}`;
  return `<div class="vm-room${built ? '' : ' unbuilt'}" style="--accent:${def.color}" data-key="room-${def.id}">
    <div class="vm-room-top">
      <span class="vm-room-glyph">${def.glyph}</span>
      <div><div class="vm-room-name">${esc(def.name)}</div><div class="vm-room-lv">${built ? `Lv ${level}/${def.maxLevel}` : 'Not built'}</div></div>
    </div>
    <div class="vm-room-text">${esc(def.text)}</div>
    <div class="vm-room-value"><b>${now}</b>${next ? ` → ${next}` : ''}</div>
    ${btn(label, 'room', { cls: built ? 'gold' : 'ghost', disabled: cost === null || v.state.te < cost, data: { id: def.id } })}
  </div>`;
}

export function roomsView(v: View): string {
  const built = roomsBuilt(v.state);
  const nextBuild = ROOM_BUILD_COSTS[built];
  const offer = offeredRooms(v.state).map((id) => ROOMS.find((r) => r.id === id)!.name).join(' or ');
  return `<p class="vm-note">Rooms are permanent and paid in ◇ TE from Folding. Two new rooms are offered at a time; your next one costs ${nextBuild ? price('te', nextBuild) : '—'} whichever you pick${offer ? ` (${offer})` : ''}. ${built}/${ROOMS.length} built.</p>
    <div class="vm-rooms">${ROOMS.map((r) => roomTile(v, r)).join('')}</div>`;
}

function goalText(def: ShipDef): string {
  if (def.source.kind !== 'earned') return '';
  const g = def.source.goal;
  return g.kind === 'depth' ? `Reach planet ${g.planets}` : g.kind === 'folds' ? `Fold ${g.folds} times` : `Finish ${g.challenges} Challenges`;
}

const SOURCE_LABEL: Readonly<Record<ShipDef['source']['kind'], string>> = { earned: 'Earned', sc: 'Shipyard', store: 'Supporter' };

/** How to get the ship: progress bar, SC buy button, store price, or owned. */
function shipAction(v: View, def: ShipDef): string {
  const { state } = v;
  if (ownsShip(state, def.id)) return `<div class="vm-ship-owned">In the fleet</div>`;
  if (def.source.kind === 'earned') {
    const [have, need] = goalProgress(state, def.source.goal);
    return `<div class="vm-ship-goal">${goalText(def)} <span>${Math.min(have, need)}/${need}</span></div>
      <div class="vm-bar"><i style="width:${Math.min(100, (100 * have) / need).toFixed(1)}%"></i></div>`;
  }
  if (def.source.kind === 'sc') {
    return btn(`Buy ${price('sc', def.source.cost)}`, 'buyShip', { cls: 'violet', disabled: !canBuyShip(state, def.id), data: { id: def.id } });
  }
  return btn(`${def.source.price} · store soon`, 'noop', { cls: 'ghost', disabled: true });
}

function shipBonus(def: ShipDef): string {
  const extra = def.source.kind === 'store' && def.source.sc ? ` · +${price('sc', def.source.sc)}` : '';
  return (Object.entries(def.mults) as [ShipEffect, number][])
    .map(([k, x]) => `<b>×${fmtFine(x)}</b> ${SHIP_EFFECT_LABEL[k]}`).join(' · ') + extra;
}

function shipTile(v: View, def: ShipDef): string {
  const owned = ownsShip(v.state, def.id);
  return `<div class="vm-room vm-ship src-${def.source.kind}${owned ? ' owned' : ''}" style="--accent:${def.color}" data-key="ship-${def.id}" data-act="openShip" data-id="${def.id}">
    <div class="vm-ship-art">${hullSvg(def, owned)}<span class="vm-ship-role">${esc(def.role)}</span></div>
    <div class="vm-room-name">${esc(def.name)}</div>
    <div class="vm-room-lv">${esc(def.text)}</div>
    <div class="vm-room-value">${shipBonus(def)}</div>
    ${shipAction(v, def)}
  </div>`;
}

/** Bottom sheet for one ship: big art on a star plate, class, lore, bonuses, how to get it. */
export function renderShipSheet(v: View, id: string): string {
  const def = SHIPS.find((s) => s.id === id);
  if (!def) return '';
  const owned = ownsShip(v.state, def.id);
  return `<div class="vm-ship-sheet src-${def.source.kind}" style="--accent:${def.color}">
    <div class="vm-ship-plate">${hullSvg(def, owned)}</div>
    <div class="vm-ship-sheet-head">
      <div><div class="vm-ship-sheet-name">${esc(def.name)}</div>
      <div class="vm-ship-sheet-role">${esc(def.role)} · ${SOURCE_LABEL[def.source.kind]}</div></div>
      ${btn('Close', 'closeSheet', { cls: 'ghost' })}
    </div>
    <p class="vm-note" style="margin:6px 0 8px">${esc(def.lore)}</p>
    <div class="vm-room-value" style="margin-bottom:10px">${shipBonus(def)}</div>
    ${shipAction(v, def)}
  </div>`;
}

export function shipsView(v: View): string {
  const owned = SHIPS.filter((s) => ownsShip(v.state, s.id)).length;
  const group = (kind: ShipDef['source']['kind'], title: string, note: string) =>
    `<div class="vm-subhead">${title}</div><p class="vm-note vm-faint">${note}</p>
    <div class="vm-rooms">${SHIPS.filter((s) => s.source.kind === kind).map((s) => shipTile(v, s)).join('')}</div>`;
  return `<p class="vm-note">Each ship is owned once, kept forever, and multiplies the whole fleet on top of Rooms. ${owned}/${SHIPS.length} in the fleet.</p>
    ${group('earned', 'Earned', 'Free, for milestones across all your galaxies.')}
    ${group('sc', 'Bought with SC', 'Stellar Cores from Folds and the rover.')}
    ${group('store', 'Store', 'Supporter hulls. The store is not open in the prototype.')}`;
}

export function crewView(v: View): string {
  const { state } = v;
  const classroom = roomMult(state, 'prospector');
  // Your crew first, unassigned before assigned so the ones to place are on top.
  const owned = PROSPECTORS.filter((def) => state.prospectors[def.id]);
  const assignedAt = (id: string) => Object.entries(state.assignments).find(([, pid]) => pid === id)?.[0];
  owned.sort((x, y) => Number(assignedAt(x.id) !== undefined) - Number(assignedAt(y.id) !== undefined));
  const cards = owned.map((def) => {
    const level = state.prospectors[def.id]!;
    const color = PROSPECTOR_RARITY_COLOR[def.rarity];
    const at = assignedAt(def.id);
    const bonus = prospectorBonus(def, level) * classroom;
    return `<div class="vm-row" style="--accent:${color}" data-key="pr-${def.id}">
      <div class="vm-row-main">
        <div class="vm-row-name" style="color:${color}">${esc(def.name)} <span class="vm-lv">Lv ${level}</span></div>
        <div class="vm-row-effect">${PROSPECTOR_ROLE_LABEL[def.role]} · ×${fmtFine(1 + bonus)} ${PLANET_STAT_LABEL[def.role]} · ${at !== undefined ? `on <b>${PLANETS[Number(at)]!.name}</b>` : '<span style="color:var(--gold)">unassigned</span>'}</div>
      </div>
    </div>`;
  }).join('');
  // Everyone still to find, as one line by rarity rather than a wall of "???" cards.
  const missing = PROSPECTORS.filter((def) => !state.prospectors[def.id]);
  const byRarity = [...new Set(missing.map((d) => d.rarity))]
    .map((r) => `<span style="color:${PROSPECTOR_RARITY_COLOR[r]}">${missing.filter((d) => d.rarity === r).length} ${r}</span>`).join(' · ');
  const left = missing.length ? `<p class="vm-note vm-faint" style="margin-top:8px">Still to find: ${byRarity}. Recruit or send the rover to find them.</p>` : '';
  return `<p class="vm-note">Prospectors boost a single planet. Recruit with ★ SC, then assign one per planet from the planet card. A duplicate recruit levels the one you have.</p>
    ${btn(`Recruit · ${price('sc', RECRUIT_COST_SC)}`, 'recruit', { cls: 'violet wide', disabled: state.sc < RECRUIT_COST_SC })}
    ${cards ? `<div class="vm-rows" style="margin-top:8px">${cards}</div>` : '<p class="vm-note vm-faint" style="margin-top:8px">No crew yet.</p>'}
    ${left}`;
}

export function fittingsView(v: View): string {
  const { state } = v;
  const cost = socketCost(state.sockets);
  const fitted = ITEMS.filter((d) => fitLevel(state.fittings[d.id] ?? 0) > 0);
  const rows = fitted.map((def) => {
    const copies = state.fittings[def.id] ?? 0;
    const level = fitLevel(copies);
    const on = state.socketed.includes(def.id);
    const nextAt = level < FIT_MAX_LEVEL ? FIT_THRESHOLDS[level + 1]! : copies;
    const bonus = fitBonus(def.rarity!, level);
    return `<div class="vm-row${on ? ' selected' : ''}" style="--accent:${def.color}" data-key="fit-${def.id}">
      <div class="vm-row-main">
        <div class="vm-row-name">${resName(def.id)} <span class="vm-lv violet">Lv ${level}</span></div>
        <div class="vm-row-effect"><b>${(def.fits ?? []).map((s) => `+${Math.round(bonus * 100)}% ${FLEET_STAT_LABEL[s]}`).join(' · ')}</b> · ${copies}/${nextAt} copies</div>
        <div class="vm-bar gold"><i style="width:${Math.min(100, (100 * copies) / nextAt).toFixed(1)}%"></i></div>
      </div>
      ${btn(on ? 'On' : 'Socket', 'socket', { cls: on ? 'on' : 'ghost', disabled: !on && state.socketed.length >= state.sockets, data: { id: def.id } })}
    </div>`;
  }).join('');
  const found = ITEMS.filter((d) => state.discovered.includes(d.id)).length;
  return `<p class="vm-note">Feed item copies from the Market tab (Fit) to level a fitting: 1 / 3 / 7 / 15 / 31 / 55 / 80 copies. Fittings are permanent; only socketed ones are active.</p>
    <div class="vm-chipline" style="margin-bottom:8px"><span>Sockets <b>${state.socketed.length}/${state.sockets}</b></span><span>Catalogue <b>${found}/${ITEMS.length}</b></span></div>
    <div class="vm-rows">${rows || '<p class="vm-note">No fittings yet. Craft an item, then press Fit on it in the Market.</p>'}</div>
    <div style="margin-top:8px">${cost === null ? '<span class="vm-faint">All sockets owned</span>' : btn(`Buy socket · ${price('sc', cost)}`, 'buySocket', { cls: 'violet wide', disabled: state.sc < cost })}</div>`;
}

function projectRow(v: View, id: string): string {
  const { state } = v;
  const def = projectDef(id);
  const status = projectStatus(state, id);
  const accent = { done: 'var(--green)', ready: 'var(--violet)', short: 'var(--line)', locked: 'var(--line-soft)' }[status];
  const needs = def.requires.map((r) => projectDef(r).name).join(' or ');
  const body = status === 'done'
    ? `<b>${esc(def.blurb)}</b>`
    : status === 'locked'
      ? `${esc(def.blurb)}<br><span class="vm-faint">Needs ${esc(needs)}</span>`
      : `<b>${esc(def.blurb)}</b><br>${ingredients(state, projectCost(state, id))}`;
  const action = status === 'done'
    ? '<span class="vm-lv green">Done</span>'
    : status === 'locked'
      ? `<span class="vm-lock" title="Locked">${icon('lock')}</span>`
      : btn('Research', 'project', { cls: status === 'ready' ? 'violet' : 'ghost', disabled: !canBuyProject(state, id), data: { id } });
  return `<div class="vm-row${status === 'done' ? ' done' : ''}" style="--accent:${accent}" data-key="pj-${id}">
    <div class="vm-row-main"><div class="vm-row-name">${esc(def.name)}</div><div class="vm-row-effect">${body}</div></div>${action}</div>`;
}

function projectsView(v: View): string {
  const { state } = v;
  const branches = (Object.keys(BRANCH_LABEL) as Branch[]).map((branch) => {
    const list = PROJECTS.filter((p) => p.branch === branch);
    // Available projects first, then the next locked one as a signpost; finished ones fold into one line.
    const done = list.filter((p) => projectStatus(state, p.id) === 'done');
    const open = list.filter((p) => { const st = projectStatus(state, p.id); return st === 'ready' || st === 'short'; });
    const firstLocked = list.find((p) => projectStatus(state, p.id) === 'locked');
    const shown = [...open, ...(firstLocked ? [firstLocked] : [])];
    const doneLine = done.length
      ? `<div class="vm-done-line"><span class="ok">${icon('check')}</span>${done.map((p) => `<span class="pj">${esc(p.name)}</span>`).join('')}</div>`
      : '';
    const rows = shown.length ? `<div class="vm-rows">${shown.map((p) => projectRow(v, p.id)).join('')}</div>` : '';
    return `<div class="vm-section-head violet" style="margin:12px 0 6px">${BRANCH_LABEL[branch]} <span class="aside">${done.length}/${list.length}</span></div>
      ${rows}${doneLine}`;
  }).join('');
  return `<p class="vm-note">Paid in resources, never GC, and reset when the galaxy folds. Telescopes reveal three more planets each.</p>${branches}`;
}

/** Projects tab: IPM's research tree for this galaxy (it resets at the Fold). */
export function renderProjects(v: View): string {
  return `<section class="vm-section" data-key="projects">${projectsView(v)}</section>`;
}
